/**
 * Video Autoplay Hook for React
 * Enables autoplay for HTML5 video tags and embedded YouTube/Vimeo iframes
 */
import { useEffect } from 'react';

/**
 * Custom hook to enable autoplay for videos
 * @param {React.RefObject} contentRef - Ref to container element
 * @param {string} content - Content HTML string (used as dependency)
 */
export const useVideoAutoplay = (contentRef, content) => {
  useEffect(() => {
    if (!contentRef.current) return;

    const videos = contentRef.current.querySelectorAll('video');
    videos.forEach((video) => {
      video.muted = true;
      video.playsInline = true;
      video.autoplay = true;

      video.setAttribute('autoplay', '');
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');

      const tryPlay = () => {
        video.play().catch((error) => {
          console.log('Autoplay prevented, retrying with muted:', error);
          video.muted = true;
          setTimeout(() => {
            video.play().catch((e) => {
              console.log('Autoplay still prevented:', e);
            });
          }, 100);
        });
      };

      if (video.readyState >= 3) {
        tryPlay();
      } else {
        video.addEventListener('loadedmetadata', tryPlay);
      }
    });

    const iframes = contentRef.current.querySelectorAll('iframe');
    iframes.forEach((iframe) => {
      let src = iframe.getAttribute('src') || '';

      if (src.includes('youtube.com') || src.includes('youtu.be')) {
        const separator = src.includes('?') ? '&' : '?';
        if (!src.includes('autoplay=')) {
          src += `${separator}autoplay=1&mute=1`;
        }
        iframe.setAttribute('src', src);
      } else if (src.includes('vimeo.com')) {
        const separator = src.includes('?') ? '&' : '?';
        if (!src.includes('autoplay=')) {
          src += `${separator}autoplay=1&muted=1`;
        }
        iframe.setAttribute('src', src);
      }
    });
  }, [contentRef, content]);
};

export default useVideoAutoplay;

